import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
// import './Marksheet.css';

const Marksheet = () => {
    const marksheetRef = useRef(null);
    const location = useLocation();
    const { student } = location.state || {};

    // Use dynamic data from the student object
    const studentData = student?.detailedMarks || student;

    if (!studentData) {
        return (
            <div className="marksheet-page">
                <div className="error-message">
                    <h2>No student data found</h2>
                    <p>Please go back and search for a student result.</p>
                </div>
            </div>
        );
    }

    // Download PDF function
    const downloadPDF = async () => {
        const element = marksheetRef.current;
        if (!element) return;

        try {
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgWidth = 210;
            const pageHeight = 295;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`${studentData.name}-Marksheet.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    // Print function
    const handlePrint = () => {
        window.print();
    };

    // Helper function to render subjects by group
    const renderSubjectsByGroup = (subjects, groupName) => {
        const groupSubjects = subjects.filter(subject => subject.group === groupName);
        return groupSubjects.map((subject, index) => (
            <tr key={index}>
                <td>{subject.name}</td>
                <td>{subject.maxMarks}</td>
                <td>{subject.theory || ''}</td>
                <td>{subject.practical || ''}</td>
                <td>{subject.total || ''}</td>
            </tr>
        ));
    };

    // Calculate current date for display
    const getCurrentDate = () => {
        const now = new Date();
        return `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()}`;
    };

    return (
        <div className="marksheet-page">
            {/* Action Buttons */}
            <div className="action-buttons">
                <button onClick={downloadPDF} className="btn btn-download">
                    📄 Download PDF
                </button>
                <button onClick={handlePrint} className="btn btn-print">
                    🖨️ Print
                </button>
            </div>

            {/* Dynamic Marksheet Content */}
            <div ref={marksheetRef} className="marksheet-container">

                <div className="university-header">
                    <h2>Mahatma Jyotiba Phule</h2>
                    <h3>Rohilkhand University, Bareilly</h3>
                    <h3>STATEMENT OF MARKS</h3>
                </div>

                <div className="student-info">
                    <h3>BACHELOR OF COMMERCE</h3>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="label">Name (Mr./Km./Smt.) :</span>
                            <span className="value">{studentData.name}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Enrollment No. :</span>
                            <span className="value">{studentData.enrollmentNo}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Father's Name :</span>
                            <span className="value">{studentData.fatherName}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Roll No. :</span>
                            <span className="value">{studentData.rollNumber}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Mother's Name :</span>
                            <span className="value">{studentData.motherName}</span>
                        </div>
                    </div>
                </div>

                {/* First Year Marks */}
                {studentData.firstYear && (
                    <div className="year-section">
                        <div className="year-header">
                            <h4>B.Com. {studentData.firstYear.year}</h4>
                            <span>{studentData.firstYear.examYear} Regular Roll No.: {studentData.firstYear.rollNo}</span>
                        </div>
                        <div className="college-info">
                            College: {studentData.college}
                        </div>
                        
                        <table className="marks-table">
                            <thead>
                                <tr>
                                    <th>PAPERS/SUBJECT OFFERED</th>
                                    <th>MAX. MARKS TH.</th>
                                    <th>MARKS OBTAINED TH.</th>
                                    <th>MARKS OBTAINED PR./VIVA</th>
                                    <th>TOTAL</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td colSpan="5" className="group-header">Group - A</td></tr>
                                {renderSubjectsByGroup(studentData.firstYear.subjects, 'A')}
                                
                                <tr><td colSpan="5" className="group-header">Group - B</td></tr>
                                {renderSubjectsByGroup(studentData.firstYear.subjects, 'B')}
                                
                                <tr><td colSpan="5" className="group-header">Group - C</td></tr>
                                {renderSubjectsByGroup(studentData.firstYear.subjects, 'C')}
                            </tbody>
                        </table>
                        
                        <div className="year-result">
                            <strong>{studentData.firstYear.year} Result : {studentData.firstYear.result}</strong>
                            <strong>Total : {studentData.firstYear.totalMarks}/{studentData.firstYear.maxTotal}</strong>
                        </div>
                    </div>
                )}

                {/* Second Year Marks */}
                {studentData.secondYear && (
                    <div className="year-section">
                        <div className="year-header">
                            <h4>B.Com. {studentData.secondYear.year}</h4>
                            <span>{studentData.secondYear.examYear} Regular Roll No.: {studentData.secondYear.rollNo}</span>
                        </div>
                        <div className="college-info">
                            College: {studentData.college}
                        </div>
                        
                        <table className="marks-table">
                            <thead>
                                <tr>
                                    <th>PAPERS/SUBJECT OFFERED</th>
                                    <th>MAX. MARKS TH.</th>
                                    <th>MARKS OBTAINED TH.</th>
                                    <th>MARKS OBTAINED PR./VIVA</th>
                                    <th>TOTAL</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td colSpan="5" className="group-header">Group - A</td></tr>
                                {renderSubjectsByGroup(studentData.secondYear.subjects, 'A')}
                                
                                <tr><td colSpan="5" className="group-header">Group - B</td></tr>
                                {renderSubjectsByGroup(studentData.secondYear.subjects, 'B')}
                                
                                <tr><td colSpan="5" className="group-header">Group - C</td></tr>
                                {renderSubjectsByGroup(studentData.secondYear.subjects, 'C')}
                            </tbody>
                        </table>
                        
                        <div className="year-result">
                            <strong>{studentData.secondYear.year} Result : {studentData.secondYear.result}</strong>
                            <strong>Total : {studentData.secondYear.totalMarks}/{studentData.secondYear.maxTotal}</strong>
                        </div>
                    </div>
                )}

                {/* Third Year Marks */}
                {studentData.thirdYear && (
                    <div className="year-section">
                        <div className="year-header">
                            <h4>B.Com. {studentData.thirdYear.year}</h4>
                            <span>{studentData.thirdYear.examYear} Regular Roll No.: {studentData.thirdYear.rollNo}</span>
                        </div>
                        <div className="college-info">
                            College: {studentData.college}
                        </div>
                        
                        <table className="marks-table">
                            <thead>
                                <tr>
                                    <th>PAPERS/SUBJECT OFFERED</th>
                                    <th>MAX. MARKS TH.</th>
                                    <th>MARKS OBTAINED TH.</th>
                                    <th>MARKS OBTAINED PR./VIVA</th>
                                    <th>TOTAL</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td colSpan="5" className="group-header">Group - A</td></tr>
                                {renderSubjectsByGroup(studentData.thirdYear.subjects, 'A')}
                                
                                <tr><td colSpan="5" className="group-header">Group - B</td></tr>
                                {renderSubjectsByGroup(studentData.thirdYear.subjects, 'B')}
                                
                                <tr><td colSpan="5" className="group-header">Group - C</td></tr>
                                {renderSubjectsByGroup(studentData.thirdYear.subjects, 'C')}
                            </tbody>
                        </table>
                        
                        <div className="year-result">
                            <strong>{studentData.thirdYear.year} Total {studentData.thirdYear.totalMarks}/{studentData.thirdYear.maxTotal}</strong>
                        </div>
                    </div>
                )}

                {/* Final Result */}
                {studentData.finalResult && (
                    <div className="final-result">
                        <div className="grand-total">
                            <strong>Grand Total {studentData.finalResult.grandTotal}/{studentData.finalResult.maxGrandTotal}</strong>
                        </div>
                        <div className="result-info">
                            <span><strong>Result: {studentData.finalResult.result}</strong></span>
                            <span><strong>Division: {studentData.finalResult.division}</strong></span>
                        </div>
                    </div>
                )}

                {/* Qualifying Subjects */}
                {studentData.qualifyingSubjects && studentData.qualifyingSubjects.length > 0 && (
                    <div className="qualifying-subjects">
                        <h4>Qualifying Subject(s)</h4>
                        <div className="qualifying-grid">
                            {studentData.qualifyingSubjects.map((subject, index) => (
                                <div key={index} className="qualifying-item">
                                    <span>{subject.name}</span>
                                    <span>{subject.marks}</span>
                                </div>
                            ))}
                        </div>
                        {studentData.status && (
                            <div className="status">
                                <div>Status: First Year = {studentData.status.firstYear}</div>
                                <div>Second Year = {studentData.status.secondYear}</div>
                                <div>Third Year = {studentData.status.thirdYear}</div>
                            </div>
                        )}
                    </div>
                )}

                <div className="signature">
                    <p>Dr. Pankaj Kumar Srivastava</p>
                </div>
            </div>
        </div>
    );
};

export default Marksheet;